import {FormEvent, useState} from 'react'
import {Card, Form, Button, Spinner, Row, Col} from 'react-bootstrap'
import {useQuery, useLazyQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {
  TWILIO_PROVISIONING_STATUS,
  MY_PROVISIONED_NUMBERS,
  TWILIO_AVAILABLE_NUMBERS,
} from '../../gql/queries/twilioNumbers'
import {
  PURCHASE_PROVISIONED_NUMBER,
  RELEASE_PROVISIONED_NUMBER,
  SET_OUTBOUND_CID,
} from '../../gql/mutations/twilioNumbers'
import {MY_BILLING} from '../../gql/queries/twilioBilling'
import {US_STATES} from '../../helpers/catalogs'
import {AssignNumberModal} from './components/AssignNumberModal'

type ProvisionedNumber = {
  id: string
  e164: string
  areaCode: string
  friendlyName: string
  status: string
  trunkAttached: boolean
  didCreated: boolean
  isOutboundCid: boolean
  created: string
  routeMode?: string
  ownerName?: string
  assignedUserId?: string
  assignedUserName?: string
  assignedGroupId?: string
  assignedGroupName?: string
}

type AvailableNumber = {
  phoneNumber: string
  friendlyName: string
  locality: string
  region: string
  areaCode: string
  voice: boolean
  sms: boolean
}

// +1XXXXXXXXXX -> +1 (XXX) XXX-XXXX
const formatE164 = (e164: string) => {
  const m = e164?.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : e164
}

export const NumbersPage = () => {
  const {data: statusData, loading: statusLoading, refetch: refetchStatus} = useQuery(
    TWILIO_PROVISIONING_STATUS,
    {fetchPolicy: 'network-only'}
  )
  const {data: minesData, loading: minesLoading, refetch: refetchMine} = useQuery(
    MY_PROVISIONED_NUMBERS,
    {fetchPolicy: 'network-only'}
  )
  const {data: billingData} = useQuery(MY_BILLING, {fetchPolicy: 'cache-and-network'})
  const billing = billingData?.myBilling

  const status = statusData?.twilioProvisioningStatus
  const enabled = !!status?.enabled
  const remaining: number = status?.remaining ?? 0
  const myNumbers: ProvisionedNumber[] = minesData?.myProvisionedNumbers ?? []

  const [stateCode, setStateCode] = useState('')
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 10
  const [runSearch, {data: searchData, loading: searching, called: searchCalled}] = useLazyQuery(
    TWILIO_AVAILABLE_NUMBERS,
    {fetchPolicy: 'network-only'}
  )
  const ownedSet = new Set(myNumbers.map((n) => n.e164))
  const available: AvailableNumber[] = (searchData?.twilioAvailableNumbers ?? []).filter(
    (a: AvailableNumber) => !ownedSet.has(a.phoneNumber)
  )
  const pageCount = Math.max(1, Math.ceil(available.length / PAGE_SIZE))
  const pageItems = available.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const [purchase, {loading: purchasing}] = useMutation(PURCHASE_PROVISIONED_NUMBER)
  const [release, {loading: releasing}] = useMutation(RELEASE_PROVISIONED_NUMBER)
  const [setOutbound, {loading: settingCid}] = useMutation(SET_OUTBOUND_CID)
  const [busyKey, setBusyKey] = useState('')
  const [assignTarget, setAssignTarget] = useState<ProvisionedNumber | null>(null)

  const showWarnings = (warnings?: string[]) => (warnings || []).forEach((w) => toast.warning(w))

  const handleSearch = (e?: FormEvent) => {
    e?.preventDefault()
    if (!stateCode) {
      toast.warning('Select a state first.')
      return
    }
    setPage(1)
    runSearch({variables: {state: stateCode, country: 'US', limit: 30}}).catch((err: any) =>
      toast.error(err?.message || 'Search failed')
    )
  }

  const handleBuy = async (num: AvailableNumber) => {
    if (remaining <= 0) return
    setBusyKey(num.phoneNumber)
    try {
      const {data} = await purchase({variables: {phoneNumber: num.phoneNumber}})
      const res = data?.purchaseProvisionedNumber
      if (res?.success) {
        toast.success(res.message || `${num.phoneNumber} acquired.`)
        showWarnings(res.warnings)
        await Promise.all([refetchMine(), refetchStatus()])
      }
    } catch (err: any) {
      toast.error(err?.message || 'Purchase failed')
    } finally {
      setBusyKey('')
    }
  }

  const handleSetOutbound = async (num: ProvisionedNumber) => {
    setBusyKey(num.id)
    try {
      const {data} = await setOutbound({variables: {id: num.id}})
      if (data?.setOutboundCid?.success) {
        toast.success(`${formatE164(num.e164)} is now your outbound Caller ID.`)
        await refetchMine()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not set outbound Caller ID')
    } finally {
      setBusyKey('')
    }
  }

  const handleRelease = async (num: ProvisionedNumber) => {
    if (
      !window.confirm(
        `Release ${formatE164(num.e164)}? This gives up the number and stops inbound calls to it.`
      )
    )
      return
    setBusyKey(num.id)
    try {
      const {data} = await release({variables: {id: num.id}})
      const res = data?.releaseProvisionedNumber
      if (res?.success) {
        toast.success(res.message || 'Number released.')
        showWarnings(res.warnings)
        await Promise.all([refetchMine(), refetchStatus()])
      }
    } catch (err: any) {
      toast.error(err?.message || 'Release failed')
    } finally {
      setBusyKey('')
    }
  }

  if (statusLoading) {
    return (
      <div className='d-flex justify-content-center py-10'>
        <Spinner animation='border' />
      </div>
    )
  }

  if (!enabled) {
    return (
      <Card className='mt-5'>
        <Card.Body className='text-center py-10'>
          <h4 className='mb-3'>Number self-service isn't enabled</h4>
          <p className='text-muted mb-0'>
            Ask an administrator to enable Twilio number provisioning for your account in
            Channel Config.
          </p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <div className='mt-5'>
      <div className='d-flex align-items-center justify-content-between mb-5 flex-wrap gap-3'>
        <div>
          <h2 className='mb-1'>My Numbers</h2>
          <span className='text-muted'>Personal Caller IDs for inbound and outbound calls.</span>
        </div>
        <span
          className={`badge fs-6 px-4 py-2 ${
            remaining > 0 ? 'badge-light-primary' : 'badge-light-secondary'
          }`}
        >
          {status?.activeCount ?? 0} of {status?.maxNumbers ?? 0} used
        </span>
      </div>

      {billing && (billing.numbers?.length ?? 0) > 0 && (
        <Card className='mb-6'>
          <Card.Body className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
            <div>
              <div className='text-muted fs-7 text-uppercase'>Your charges this month</div>
              <div className='fs-2 fw-bold'>${Number(billing.charge_total).toFixed(2)}</div>
              <span className='text-muted fs-8'>number rental + voice usage</span>
            </div>
            <div className='text-end'>
              {billing.numbers.map((n: any) => (
                <div key={n.e164} className='fs-8 text-muted'>
                  {formatE164(n.e164)}: <span className='fw-semibold'>${Number(n.charge_total).toFixed(2)}</span>{' '}
                  ({n.inbound_calls + n.outbound_calls} calls)
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Your numbers */}
      <Card className='mb-6'>
        <Card.Header>
          <Card.Title>Your numbers</Card.Title>
        </Card.Header>
        <Card.Body>
          {minesLoading ? (
            <Spinner animation='border' size='sm' />
          ) : myNumbers.length === 0 ? (
            <p className='text-muted mb-0'>You don't have any numbers yet. Get one below.</p>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed align-middle gy-3'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>Number</th>
                    <th>Area</th>
                    <th>Assigned to</th>
                    <th>Routing</th>
                    <th>Outbound CID</th>
                    <th className='text-end'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {myNumbers.map((n) => (
                    <tr key={n.id}>
                      <td className='fw-bold'>{formatE164(n.e164)}</td>
                      <td>{n.areaCode || '—'}</td>
                      <td>
                        {n.assignedGroupName ? (
                          <span className='badge badge-light-info'>Group: {n.assignedGroupName}</span>
                        ) : n.assignedUserName ? (
                          <span className='badge badge-light-primary'>{n.assignedUserName}</span>
                        ) : (
                          <span className='text-muted'>—</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge me-2 ${
                            n.trunkAttached ? 'badge-light-success' : 'badge-light-warning'
                          }`}
                        >
                          {n.trunkAttached ? 'Trunk ✓' : 'Trunk pending'}
                        </span>
                        <span
                          className={`badge ${
                            n.didCreated ? 'badge-light-success' : 'badge-light-warning'
                          }`}
                        >
                          {n.didCreated ? 'Inbound ✓' : 'Inbound pending'}
                        </span>
                      </td>
                      <td>
                        {n.isOutboundCid ? (
                          <span className='badge badge-light-success'>Outbound ✓</span>
                        ) : (
                          <Button
                            variant='light-primary'
                            size='sm'
                            disabled={settingCid && busyKey === n.id}
                            onClick={() => handleSetOutbound(n)}
                          >
                            {settingCid && busyKey === n.id ? (
                              <Spinner animation='border' size='sm' />
                            ) : (
                              'Use for outbound'
                            )}
                          </Button>
                        )}
                      </td>
                      <td className='text-end'>
                        <Button
                          variant='light-primary'
                          size='sm'
                          className='me-2'
                          onClick={() => setAssignTarget(n)}
                        >
                          Assign
                        </Button>
                        <Button
                          variant='light-danger'
                          size='sm'
                          disabled={releasing && busyKey === n.id}
                          onClick={() => handleRelease(n)}
                        >
                          {releasing && busyKey === n.id ? (
                            <Spinner animation='border' size='sm' />
                          ) : (
                            'Release'
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Get a new number */}
      <Card>
        <Card.Header>
          <Card.Title>Get a new number</Card.Title>
        </Card.Header>
        <Card.Body>
          {remaining <= 0 && (
            <div className='alert alert-warning'>
              You've reached your limit of {status?.maxNumbers} number(s). Release one to get
              another, or ask an admin to raise your limit.
            </div>
          )}
          <Form onSubmit={handleSearch} className='mb-5'>
            <Row className='g-3 align-items-end'>
              <Col xs='auto'>
                <Form.Label>State</Form.Label>
                <Form.Select
                  style={{minWidth: 240}}
                  value={stateCode}
                  onChange={(e: any) => setStateCode(e.target.value)}
                >
                  <option value=''>Select a state…</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs='auto'>
                <Button type='submit' variant='primary' disabled={searching}>
                  {searching ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      Searching…
                    </>
                  ) : (
                    'Search'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>

          {searchCalled && !searching && (
            available.length === 0 ? (
              <p className='text-muted mb-0'>
                No numbers available for that state right now. Try another.
              </p>
            ) : (
              <div className='table-responsive'>
                <table className='table table-row-dashed align-middle gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>Number</th>
                      <th>Location</th>
                      <th className='text-end'>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((a) => (
                      <tr key={a.phoneNumber}>
                        <td className='fw-bold'>{formatE164(a.phoneNumber)}</td>
                        <td>{[a.locality, a.region].filter(Boolean).join(', ') || '—'}</td>
                        <td className='text-end'>
                          <Button
                            variant='primary'
                            size='sm'
                            disabled={remaining <= 0 || (purchasing && busyKey === a.phoneNumber)}
                            onClick={() => handleBuy(a)}
                          >
                            {purchasing && busyKey === a.phoneNumber ? (
                              <Spinner animation='border' size='sm' />
                            ) : (
                              'Buy'
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pageCount > 1 && (
                  <div className='d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2'>
                    <span className='text-muted'>
                      Showing {(page - 1) * PAGE_SIZE + 1}–
                      {Math.min(page * PAGE_SIZE, available.length)} of {available.length}
                    </span>
                    <div className='d-flex gap-2'>
                      <Button
                        variant='light'
                        size='sm'
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <span className='align-self-center text-muted'>
                        {page} / {pageCount}
                      </span>
                      <Button
                        variant='light'
                        size='sm'
                        disabled={page >= pageCount}
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </Card.Body>
      </Card>

      <AssignNumberModal
        show={!!assignTarget}
        onHide={() => setAssignTarget(null)}
        numberId={assignTarget?.id ?? null}
        numberLabel={assignTarget ? formatE164(assignTarget.e164) : ''}
        currentAssignee={
          assignTarget?.assignedGroupName
            ? `Group: ${assignTarget.assignedGroupName}`
            : assignTarget?.assignedUserName || ''
        }
        onAssigned={() => refetchMine()}
      />
    </div>
  )
}
